import type { SlotProps } from "@radix-ui/react-slot";
import type { EditorAction, RegionEditorTab } from "../editor/reducers";
import type { RegionShortcutsHandlerFragment } from "../client/generated";
import { Slot } from "@radix-ui/react-slot";
import { useCallback } from "react";
import { useDeleteRegionViaShortcutMutation } from "../client/generated";
import { getTableFromRegion } from "../tables/utils";

type Props = SlotProps & {
  fragment: RegionShortcutsHandlerFragment;
  dispatch: React.Dispatch<EditorAction>;
};

export function RegionShortcutsHandler({
  fragment,
  dispatch,
  children,
  className,
  ...props
}: Props) {
  const [deleteRegion] = useDeleteRegionViaShortcutMutation({
    variables: { regionId: fragment.id },
    optimisticResponse: {
      deleteRegion: {
        userErrors: [],
        region: {
          __typename: "Region",
          id: fragment.id,
        },
      },
    },
    update(cache) {
      const id = cache.identify(fragment);
      cache.evict({ id });
      cache.gc();
    },
  });

  const onDelete = useCallback(async () => {
    const { errors, data } = await deleteRegion();

    if (errors) {
      console.error("Error deleting region:", errors);
    } else if (data?.deleteRegion.userErrors) {
      console.error(
        "User error deleting region:",
        data.deleteRegion.userErrors,
      );
    } else {
      dispatch({ type: "deselectRegion" });
    }
  }, [deleteRegion, dispatch]);

  const convertRegion = (tab: RegionEditorTab) => {
    dispatch({
      type: "switchToRegionTab",
      tab,
      regionId: fragment.id,
    });
  };

  const onKeyDown = async (event: React.KeyboardEvent<HTMLElement>) => {
    switch (event.key) {
      case "Delete":
      case "Backspace":
        onDelete();
        break;
      case "h":
        convertRegion("heading");
        break;
      case "p":
        convertRegion("text");
        break;
      case "m":
        convertRegion("formula");
        break;
      case "t":
        dispatch({
          type: "switchToRegionTab",
          tab: "table",
          regionId: fragment.id,
          table: getTableFromRegion(fragment),
        });
        break;
      case "i":
        convertRegion("image");
        break;
      case "f":
        convertRegion("figure");
        break;
      case "c":
        convertRegion("caption");
        break;
      case "l":
        convertRegion("list");
        break;
      case "b":
        convertRegion("listItem");
        break;
      case "s":
        convertRegion("sublist");
        break;
      case "a":
        convertRegion("artifact");
        break;
      case "d":
        convertRegion("toc");
        break;
    }
  };

  return (
    <Slot {...props}>
      <Slot className={className} tabIndex={0} onKeyDown={onKeyDown}>
        {children}
      </Slot>
    </Slot>
  );
}
